/**
 * BugReportModal - Modal form for submitting bug reports or feature requests
 */
import React, { useState } from 'react';
import { useBugCapture } from '../../hooks/useBugCapture';
import { bugsApi } from '../../api/bugs';
import CaptureProgress from './CaptureProgress';
import './BugReporter.css';

// CRA inlines REACT_APP_VERSION at build time. Unset builds send 0.1.0.
const APP_VERSION = process.env.REACT_APP_VERSION || '0.1.0';

function BugReportModal({ onClose, onSuccess, preCapturedScreenshot }) {
  const { captureBrowserContext } = useBugCapture();

  const [reportType, setReportType] = useState('bug');

  // Bug report fields
  const [bugData, setBugData] = useState({
    whatWentWrong: '',
    whatTrying: '',
    reproductionRate: 'once',
    reproductionSteps: '',
    userImpact: 'annoying'
  });

  // Feature request fields
  const [featureData, setFeatureData] = useState({
    featureDescription: '',
    workflowBenefit: '',
    priorityToUser: 'nice_to_have',
    currentWorkaround: ''
  });

  const [includeScreenshot, setIncludeScreenshot] = useState(true);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captureStep, setCaptureStep] = useState(null);
  const [error, setError] = useState(null);

  const handleBugChange = (e) => {
    const { name, value } = e.target;
    setBugData(prev => ({ ...prev, [name]: value }));
  };

  const handleFeatureChange = (e) => {
    const { name, value } = e.target;
    setFeatureData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      if (reportType === 'bug') {
        // The screenshot was taken when the writer opened this report.
        // Logs stay on the device. Only the form, the page, the version,
        // and that screenshot are sent.
        setCaptureStep('screenshot');

        setCaptureStep('submitting');
        const response = await bugsApi.submit({
          reportType: 'bug',
          ...bugData,
          ...(includeScreenshot ? { screenshot: preCapturedScreenshot } : {}),
          browserContext: captureBrowserContext(),
          appVersion: APP_VERSION,
        });

        setCaptureStep('complete');
        setTimeout(() => {
          onSuccess?.(response);
        }, 1500);
      } else {
        // Feature request: only capture browser context
        setCaptureStep('submitting');

        const response = await bugsApi.submit({
          reportType: 'feature',
          ...featureData,
          browserContext: captureBrowserContext(),
          appVersion: APP_VERSION,
        });

        setCaptureStep('complete');
        setTimeout(() => {
          onSuccess?.(response);
        }, 1500);
      }

    } catch (err) {
      console.error('Submission failed:', err);
      setError(err.message || `Failed to submit ${reportType === 'bug' ? 'bug report' : 'feature request'}`);
      setCaptureStep(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  const isBugValid = bugData.whatWentWrong.trim() && bugData.whatTrying.trim();
  const isFeatureValid = featureData.featureDescription.trim() && featureData.workflowBenefit.trim();
  const isValid = reportType === 'bug' ? isBugValid : isFeatureValid;

  return (
    <div className="bug-report-overlay" onClick={handleBackdropClick}>
      <div className="bug-report-modal">
        <div className="bug-report-modal__header">
          <h2>How can we help?</h2>
          <button
            className="bug-report-modal__close"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {captureStep ? (
          <div className="bug-report-modal__progress">
            <CaptureProgress
              currentStep={captureStep}
              error={error}
              isFeatureRequest={reportType === 'feature'}
            />
          </div>
        ) : (
          <form className="bug-report-form" onSubmit={handleSubmit}>
            {/* Report Type Toggle */}
            <div className="bug-report-form__type-toggle">
              <button
                type="button"
                className={`type-toggle__option ${reportType === 'bug' ? 'active' : ''}`}
                onClick={() => setReportType('bug')}
              >
                Something isn't working
              </button>
              <button
                type="button"
                className={`type-toggle__option ${reportType === 'feature' ? 'active' : ''}`}
                onClick={() => setReportType('feature')}
              >
                I have an idea
              </button>
            </div>

            {reportType === 'bug' ? (
              /* Bug Report Fields */
              <>
                <div className="bug-report-form__field">
                  <label htmlFor="whatWentWrong">What went wrong? *</label>
                  <textarea
                    id="whatWentWrong"
                    name="whatWentWrong"
                    value={bugData.whatWentWrong}
                    onChange={handleBugChange}
                    placeholder="Describe the issue you encountered..."
                    required
                    rows={3}
                  />
                </div>

                <div className="bug-report-form__field">
                  <label htmlFor="whatTrying">What were you trying to do? *</label>
                  <textarea
                    id="whatTrying"
                    name="whatTrying"
                    value={bugData.whatTrying}
                    onChange={handleBugChange}
                    placeholder="Describe what you were trying to accomplish..."
                    required
                    rows={3}
                  />
                </div>

                <div className="bug-report-form__row">
                  <div className="bug-report-form__field">
                    <label>How often does this happen?</label>
                    <div className="bug-report-form__radio-group">
                      {[
                        { value: 'always', label: 'Always' },
                        { value: 'sometimes', label: 'Sometimes' },
                        { value: 'once', label: 'Just once' }
                      ].map(opt => (
                        <label key={opt.value} className="bug-report-form__radio">
                          <input
                            type="radio"
                            name="reproductionRate"
                            value={opt.value}
                            checked={bugData.reproductionRate === opt.value}
                            onChange={handleBugChange}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="bug-report-form__field">
                    <label>How much does this affect you?</label>
                    <div className="bug-report-form__radio-group">
                      {[
                        { value: 'blocking', label: 'Blocking' },
                        { value: 'annoying', label: 'Annoying' },
                        { value: 'minor', label: 'Minor' }
                      ].map(opt => (
                        <label key={opt.value} className="bug-report-form__radio">
                          <input
                            type="radio"
                            name="userImpact"
                            value={opt.value}
                            checked={bugData.userImpact === opt.value}
                            onChange={handleBugChange}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {bugData.reproductionRate !== 'once' && (
                  <div className="bug-report-form__field">
                    <label htmlFor="reproductionSteps">Steps to reproduce (optional)</label>
                    <textarea
                      id="reproductionSteps"
                      name="reproductionSteps"
                      value={bugData.reproductionSteps}
                      onChange={handleBugChange}
                      placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
                      rows={4}
                    />
                  </div>
                )}

                <div className="bug-report-form__field">
                  <label className="bug-report-form__radio" htmlFor="includeScreenshot">
                    <input
                      id="includeScreenshot"
                      type="checkbox"
                      checked={includeScreenshot}
                      onChange={(e) => setIncludeScreenshot(e.target.checked)}
                      aria-describedby="includeScreenshotHint"
                    />
                    Include a screenshot
                  </label>
                  <p id="includeScreenshotHint" className="bug-report-form__note">
                    It shows what's on your screen, which may include your writing. Untick to leave it out.
                  </p>
                </div>
              </>
            ) : (
              /* Feature Request Fields */
              <>
                <div className="bug-report-form__field">
                  <label htmlFor="featureDescription">What feature would you like? *</label>
                  <textarea
                    id="featureDescription"
                    name="featureDescription"
                    value={featureData.featureDescription}
                    onChange={handleFeatureChange}
                    placeholder="Describe the feature you'd like to see..."
                    required
                    rows={3}
                  />
                </div>

                <div className="bug-report-form__field">
                  <label htmlFor="workflowBenefit">How would this help your workflow? *</label>
                  <textarea
                    id="workflowBenefit"
                    name="workflowBenefit"
                    value={featureData.workflowBenefit}
                    onChange={handleFeatureChange}
                    placeholder="Explain how this feature would improve your experience..."
                    required
                    rows={3}
                  />
                </div>

                <div className="bug-report-form__field">
                  <label>How important is this to you?</label>
                  <div className="bug-report-form__radio-group bug-report-form__radio-group--vertical">
                    {[
                      { value: 'essential', label: 'Essential - I really need this' },
                      { value: 'nice_to_have', label: 'Nice to have - would improve my experience' },
                      { value: 'just_an_idea', label: 'Just an idea - no pressure' }
                    ].map(opt => (
                      <label key={opt.value} className="bug-report-form__radio">
                        <input
                          type="radio"
                          name="priorityToUser"
                          value={opt.value}
                          checked={featureData.priorityToUser === opt.value}
                          onChange={handleFeatureChange}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="bug-report-form__field">
                  <label htmlFor="currentWorkaround">Current workaround (optional)</label>
                  <textarea
                    id="currentWorkaround"
                    name="currentWorkaround"
                    value={featureData.currentWorkaround}
                    onChange={handleFeatureChange}
                    placeholder="How do you handle this today, if at all?"
                    rows={2}
                  />
                </div>
              </>
            )}

            {error && (
              <div className="bug-report-form__error">
                {error}
              </div>
            )}

            <div className="bug-report-form__actions">
              <button
                type="button"
                className="bug-report-form__cancel"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bug-report-form__submit"
                disabled={!isValid || isSubmitting}
              >
                {isSubmitting
                  ? 'Submitting...'
                  : reportType === 'bug'
                    ? 'Submit Bug Report'
                    : 'Submit Feature Request'}
              </button>
            </div>

            <p className="bug-report-form__note">
              {reportType === 'bug'
                ? includeScreenshot
                  ? 'A screenshot was captured when you opened this report.'
                  : "The screenshot won't be sent."
                : 'The page you are on is included with this request.'}
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

export default BugReportModal;
